import { useState, useEffect, useCallback, useRef, type ClipboardEvent } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@/components/ui/input-otp'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useTheme } from '@/lib/contexts/ThemeContext'
import { verify, signIn, register, registerV3 } from '@/lib/api/auth'
import { toast } from 'sonner'
import { track } from '@/lib/utils/track'
import { markOtpConfirmed } from '@/lib/utils/authState'
import { getAcquisitionContext } from '@/lib/utils/acquisitionContext'
import { executeRecaptcha } from '@/lib/utils/recaptcha'
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { MobileTrustStrip } from '@/components/auth/AuthProof'
import HumblyticsLogo from '@/assets/humblytics-web-app-logo.svg'
import { loadIntercom, showIntercom } from '@/lib/utils/intercom'

// Helper to extract digits from any string (handles spaces, dashes, etc.)
function extractDigits(str: string): string {
  return str.replace(/\D/g, '')
}

export default function Verify() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setToken } = useAuth()
  const { setTheme } = useTheme()

  const email = searchParams.get('email') || ''
  const from = searchParams.get('from') || '/dashboard'
  const name = searchParams.get('name') || ''
  const isNewUserVerification = searchParams.get('t') === 'n' || Boolean(name)

  const [verificationCode, setVerificationCode] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSkipping, setIsSkipping] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [time, setTime] = useState(60)
  const [elapsed, setElapsed] = useState(0)
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)
  const [resendCount, setResendCount] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  /** After 20s, surface spam guidance before the 60s resend unlocks. */
  const showEarlyHelp = elapsed >= 20 || resendCount >= 1

  useEffect(() => {
    document.title = 'Verify Email - Humblytics';
  }, []);

  // Force light mode — always set on mount, restore original on unmount
  useEffect(() => {
    const stored = localStorage.getItem('theme') as 'light' | 'dark' | null
    setTheme('light')
    return () => {
      if (stored && stored !== 'light') {
        setTheme(stored)
      }
    }
  }, [])

  // Boot Intercom with launcher hidden — pre-auth, opened only on explicit help click
  useEffect(() => {
    loadIntercom(email || undefined, undefined, true)
  }, [])

  // Track verification page view for dropoff funnel
  useEffect(() => {
    track('verify_page_viewed', null, { is_new_user: isNewUserVerification })
  }, [])

  // Resend countdown + elapsed clock (for 20s early help)
  useEffect(() => {
    startTimer()
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [])

  const startTimer = () => {
    setElapsed(0)
    const newTimer = setInterval(() => {
      setElapsed((prev) => prev + 1)
      setTime((prevTime) => {
        if (prevTime > 0) {
          return prevTime - 1
        }
        return 0
      })
    }, 1000)
    setTimer(newTimer)
  }

  const verifyEmail = useCallback(async (code: string) => {
    if (isProcessing || code.length !== 6) return

    setIsProcessing(true)
    try {
      const result = await verify(email, code)
      if (result) {
        track(result.isNew ? 'signup_completed' : 'signin_completed', result.authToken, {
          ...getAcquisitionContext(),
          auth_method: 'email',
          is_new_user: result.isNew,
        })
        markOtpConfirmed()
        setToken(result.authToken)
        navigate(result.isNew ? '/onboarding/start' : from, { replace: true })
      } else {
        setVerificationCode('')
        setIsModalOpen(true)
      }
    } catch (error) {
      console.error(error)
      toast.error('Verification failed. Please try again.')
      setVerificationCode('')
    } finally {
      setIsProcessing(false)
    }
  }, [email, from, isProcessing, navigate, setToken])

  // Auto-submit when code is complete
  const handleCodeChange = useCallback((value: string) => {
    const digitsOnly = extractDigits(value).slice(0, 6)
    setVerificationCode(digitsOnly)
    if (digitsOnly.length === 6) {
      verifyEmail(digitsOnly)
    }
  }, [verifyEmail])

  // Paste into OTP slots (spaces / dashes stripped)
  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData?.getData('text') || ''
    const digits = extractDigits(pastedData).slice(0, 6)

    if (digits.length > 0) {
      setVerificationCode(digits)
      if (digits.length === 6) {
        setTimeout(() => verifyEmail(digits), 50)
      }
    }
  }, [verifyEmail])

  // Paste-anywhere when focus isn't in another text field
  useEffect(() => {
    const onPaste = (e: globalThis.ClipboardEvent) => {
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      const pastedData = e.clipboardData?.getData('text') || ''
      const digits = extractDigits(pastedData).slice(0, 6)
      if (digits.length < 4) return
      e.preventDefault()
      setVerificationCode(digits)
      if (digits.length === 6) {
        setTimeout(() => verifyEmail(digits), 50)
      }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [verifyEmail])

  const handleSkipVerification = async () => {
    if (isSkipping) return
    setIsSkipping(true)
    try {
      const recaptchaSiteKey = import.meta.env.VITE_PUBLIC_RECAPTCHA_SITE_KEY || ''
      const recaptchaToken = await executeRecaptcha(recaptchaSiteKey, 'signup')
      const result = await registerV3(email, name || email.split('@')[0], recaptchaToken)
      if (result === 'user_exists') {
        toast.error('An account with this email already exists. Please sign in.')
        navigate(`/auth?mode=signin&email=${encodeURIComponent(email)}`)
        return
      }
      if (result && typeof result === 'object' && result.authToken) {
        track('signup_verification_skipped', result.authToken, {
          ...getAcquisitionContext(),
          auth_method: 'email',
        })
        markOtpConfirmed()
        setToken(result.authToken)
        navigate('/onboarding/start', { replace: true })
      } else {
        toast.error('Could not skip verification. Please try the code or resend.')
      }
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsSkipping(false)
    }
  }

  const resend = async () => {
    try {
      const recaptchaSiteKey = import.meta.env.VITE_PUBLIC_RECAPTCHA_SITE_KEY || ''
      const recaptchaToken = await executeRecaptcha(
        recaptchaSiteKey,
        isNewUserVerification ? 'signup' : 'auth_check'
      )

      if (isNewUserVerification) {
        const result = await register(email, name || email.split('@')[0], recaptchaToken)
        if (result && typeof result === 'object' && 'authToken' in result) {
          markOtpConfirmed()
          setToken(result.authToken)
          navigate('/onboarding/start', { replace: true })
          return
        }
        // { codeSent: true } means a new code was sent — fall through to success toast
        if (!result) throw new Error('Could not resend verification code')
      } else {
        const sent = await signIn(email, recaptchaToken)
        if (!sent) throw new Error('Could not resend verification code')
      }

      if (timer) clearInterval(timer)
      setTime(60)
      startTimer()
      const nextCount = resendCount + 1
      setResendCount(nextCount)
      track('verify_resend_clicked', null, { attempt: nextCount, is_new_user: isNewUserVerification })
      toast.success('A new code has been sent.')
    } catch {
      setTime(0)
      toast.error('Could not send a new code. Please try again.')
    }
  }

  const handleContactSupport = () => {
    track('verify_help_opened', null, { is_new_user: isNewUserVerification })
    if (typeof window !== 'undefined' && window.Intercom) {
      showIntercom()
    } else {
      window.open('https://humblytics.com/contact-us', '_blank')
    }
  }

  // Format timer display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`
  }

  return (
    <div className="relative flex min-h-svh flex-col bg-canvas text-foreground">
      <header className="px-4 pt-5 sm:px-8 sm:pt-7 lg:absolute lg:left-0 lg:top-0">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src={HumblyticsLogo} alt="" className="size-5" />
          <span className="text-base font-medium leading-5 tracking-[-0.01em]">Humblytics</span>
        </Link>
      </header>

      <main className="flex w-full flex-1 flex-col items-center justify-center gap-6 px-4 pb-8 pt-5 sm:px-8 lg:py-16">
        <div className="surface-raised flex w-full max-w-[440px] flex-col gap-7 px-5 py-7 sm:p-10">
          <div className="flex flex-col gap-2 text-left">
            <p className="text-[13px] font-medium leading-5 text-muted-foreground">
              {isNewUserVerification ? 'Almost there' : 'Welcome back'}
            </p>
            <h1 className="text-balance text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground sm:text-[28px] sm:leading-[34px]">
              {isNewUserVerification ? 'Finish creating your account' : 'Enter your sign-in code'}
            </h1>
            <p className="text-pretty text-sm leading-[22px] text-muted-foreground">
              We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>.
              If you don't see it, check your spam folder.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="relative flex w-full justify-center">
              <InputOTP
                ref={inputRef}
                maxLength={6}
                value={verificationCode}
                onChange={handleCodeChange}
                onPaste={handlePaste}
                disabled={isProcessing}
                pattern={REGEXP_ONLY_DIGITS}
                autoFocus
                containerClassName="justify-center gap-2 sm:gap-3"
              >
                <InputOTPGroup className="gap-1.5 sm:gap-2">
                  <InputOTPSlot index={0} className={otpSlotClass(verificationCode.length > 0)} />
                  <InputOTPSlot index={1} className={otpSlotClass(verificationCode.length > 1)} />
                  <InputOTPSlot index={2} className={otpSlotClass(verificationCode.length > 2)} />
                </InputOTPGroup>
                <InputOTPSeparator className="h-[1.5px] w-2 bg-neutral-300 [&>span]:hidden" />
                <InputOTPGroup className="gap-1.5 sm:gap-2">
                  <InputOTPSlot index={3} className={otpSlotClass(verificationCode.length > 3)} />
                  <InputOTPSlot index={4} className={otpSlotClass(verificationCode.length > 4)} />
                  <InputOTPSlot index={5} className={otpSlotClass(verificationCode.length > 5)} />
                </InputOTPGroup>
              </InputOTP>

              {isProcessing && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-card/70 backdrop-blur-[1px]">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span>Verifying</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex w-full flex-col gap-2">
              <Button
                onClick={() => verifyEmail(verificationCode)}
                variant="default"
                disabled={isProcessing || verificationCode.length !== 6}
                className="h-11 w-full rounded-lg px-4 text-sm font-medium shadow-none"
              >
                {isProcessing ? 'Verifying...' : 'Verify'}
              </Button>

              <Button
                onClick={() => window.open('https://mail.google.com', '_blank')}
                variant="outline"
                className="h-11 w-full gap-2 rounded-lg border-input bg-card px-4 text-sm font-medium text-foreground shadow-xs hover:border-input hover:text-foreground"
              >
                <GmailMark />
                Open Gmail
              </Button>
            </div>
          </div>

          {/* Resend section: spam help surfaces at 20s; resend unlocks at 60s */}
          <div className="flex flex-col gap-3 border-t border-border pt-5">
            <div
              className={`rounded-lg text-xs leading-[18px] transition-all duration-300 ${
                showEarlyHelp ? 'bg-muted px-3.5 py-3' : 'text-muted-foreground'
              }`}
            >
              {showEarlyHelp ? (
                <p className="mb-1 text-[13px] font-medium leading-5 text-foreground">
                  {resendCount >= 1 ? 'Still nothing? Check these:' : "Didn't get it? Check these:"}
                </p>
              ) : null}
              <ul className="flex flex-col gap-1 text-muted-foreground">
                <li>
                  Check your spam / junk and Promotions tab
                </li>
                <li>Confirm the address above is correct</li>
                <li>Codes can take up to a minute to arrive</li>
              </ul>
            </div>

            <div className="flex items-center justify-center gap-1 text-[13px] leading-5">
              <span className="text-muted-foreground">
                {resendCount >= 1 ? 'Still nothing?' : "Didn't get the code?"}
              </span>
              {time > 0 ? (
                <span className="tabular-nums text-muted-foreground">
                  Resend in {formatTime(time)}
                </span>
              ) : (
                <button
                  onClick={resend}
                  className="font-medium text-foreground underline underline-offset-2 transition-colors hover:text-muted-foreground"
                >
                  Resend
                </button>
              )}
            </div>

            {/* Contact support: visible only after 2nd resend */}
            {resendCount >= 2 && (
              <div className="flex justify-center">
                <button
                  onClick={handleContactSupport}
                  className="text-xs leading-[18px] text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
                >
                  Still stuck? Contact support
                </button>
              </div>
            )}

            {/* Skip verification: new signups only */}
            {isNewUserVerification && (
              <div className="flex justify-center">
                <button
                  onClick={handleSkipVerification}
                  disabled={isSkipping}
                  className="text-[13px] font-medium leading-5 text-foreground underline underline-offset-2 transition-colors hover:text-muted-foreground"
                >
                  {isSkipping ? 'Setting up your account…' : 'Skip email verification'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-4">
          <button
            onClick={() => {
              const back = new URLSearchParams()
              if (email) back.set('email', email)
              back.set('mode', searchParams.get('mode') || (isNewUserVerification ? 'signup' : 'signin'))
              if (from !== '/dashboard') back.set('from', from)
              navigate(`/auth?${back.toString()}`)
            }}
            className="text-[13px] font-medium leading-5 text-foreground underline-offset-4 hover:underline"
          >
            Back
          </button>

          <MobileTrustStrip />

          <div className="flex flex-wrap justify-center gap-1.5 text-xs leading-[18px] text-muted-foreground">
            <a
              href="https://www.humblytics.com/utilities/humblytics-terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              Terms
            </a>
            <span aria-hidden="true">·</span>
            <a
              href="https://www.humblytics.com/utilities/humblytics-privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              Privacy
            </a>
          </div>
        </div>
      </main>

      {/* Error Modal */}
      <AlertDialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg">Invalid code</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px]">
              The code you entered is incorrect or expired. Check your email and try again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setIsModalOpen(false)}>
              Try again
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** Code boxes (Paper): filled boxes take the input ring plus a soft shadow; empty boxes a darker ring */
function otpSlotClass(filled: boolean): string {
  return [
    'h-12 w-10 rounded-lg bg-card text-[22px] font-medium sm:h-[52px] sm:w-12',
    filled ? 'border-input shadow-xs' : 'border-neutral-300',
    'data-[active=true]:border-foreground data-[active=true]:ring-[3px] data-[active=true]:ring-foreground/10',
  ].join(' ')
}

function GmailMark() {
  return (
    <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true" className="shrink-0">
      <path d="M1 12h2.5V5.5L0 3v8a1 1 0 0 0 1 1z" fill="#4285F4" />
      <path d="M12.5 12H15a1 1 0 0 0 1-1V3l-3.5 2.5z" fill="#34A853" />
      <path d="M12.5 1v4.5L16 3V1.5C16 .3 14.6-.4 13.6.3z" fill="#FBBC04" />
      <path d="M3.5 5.5V1L8 4.4 12.5 1v4.5L8 8.9z" fill="#EA4335" />
      <path d="M0 1.5V3l3.5 2.5V1L2.4.3C1.4-.4 0 .3 0 1.5z" fill="#C5221F" />
    </svg>
  )
}
